import { Form } from '@fulcrumapp/fulcrum-core';
import MapView from '@arcgis/core/views/MapView';
import Sketch from '@arcgis/core/widgets/Sketch';
import { convertToGeoJSON } from './graphics-handler';
import initialize from './initializer';
import { GeometricShape, GeometricType } from '../types/geo';
import Providers, { Providable } from './providers';
import {
  beginEditing, correctMismatch, drawNearbyRecords, endEditing, reenableEditing, replaceShape, updateShapeColor,
} from './interactions';
import { Globals } from '../models/globals';
import { GeographicRecord } from './data-fetcher';
import Colorizer, { Colorable } from './colorizer';
import { geometriesMatch } from './geo-tools';
import { supportedGeometryTypes } from './supported-geometry-types';

// Form, RepeatableElement, Record
export interface GeometryTypeable {
  _geometryTypes: string[],
}

export type GeometryMatcher = (
  geom1: GeometricShape | undefined | null,
  geom2: GeometricShape | undefined | null
) => boolean

export const shouldUseEsriMaps = (globals: Globals, geometryTyped: GeometryTypeable) => {
  if (!globals.ldflags?.['allow-google-map-engine']) {
    return true;
  }
  const linesAndPolygonsEnabled = advancedGeometryEnabled(geometryTyped);

  return globals.flags.web_map_engine === 'ESRI' || linesAndPolygonsEnabled;
};

export const advancedGeometryEnabled = (geometryTypes: GeometryTypeable) => {
  const geometryTypesArray = geometryTypes?._geometryTypes ?? null;
  if (!geometryTypesArray || geometryTypesArray.length === 0) return false;

  const isPoint = geometryTypesArray.length === 1 && geometryTypesArray[0] === 'Point';
  const isPointOrMultiPoint = geometryTypesArray.length === 2
    && geometryTypesArray.includes('Point')
    && geometryTypesArray.includes('MultiPoint');

  return !(isPoint || isPointOrMultiPoint);
};

// Developer hack to get insight into MapView layers
const dumpLayersToConsole = (mapView: MapView) => {
  if (localStorage.getItem('dump-sketch-editor-layers') === 'true') {
    const output = ({
      declaredClass, title, visible, type, id,
    }) => ({
      declaredClass, title, visible, type, id,
    });

    const allLayers = [];
    const layers = [];

    // @ts-ignore
    mapView.allLayerViews.forEach((view) => allLayers.push(output(view.layer)));
    // @ts-ignore
    mapView.layerViews.forEach((view) => layers.push(output(view.layer)));

    console.log('mapView.allLayerViews:', allLayers);
    console.log('mapView.layerViews:', layers);
  }
};

export type InitialValues = {
  form: Form;
  geometry: GeometricShape | undefined;
}

export type FetchEditorStateHandler = () => {
  status: string,
  editable: boolean,
  loading: boolean,
  showValidationErrors: boolean,
  geometry: GeometricShape | undefined | null,
  typeMismatched: boolean,
};

export type OnGeometryChangeHandler = (geoJson: GeometricShape | null) => void

export interface EditorState {
  loading: boolean,
  showValidationErrors: boolean,
  editable: boolean,
  geometry: GeometricShape | undefined | null,
  typeMismatched?: boolean,
}

export interface Sketchable {
  init: (
    mapView: MapView,
    onGeometryChange: OnGeometryChangeHandler,
    initialValues: InitialValues,
    geometryTypes: GeometricType[],
    status?: string,
  ) => void;
  setEditingState: (editorState: EditorState, geometryMatcher?: GeometryMatcher) => void;
  setGeometryColor: (status: string) => void;
  setNearbyRecords: (nearbyRecords: GeographicRecord[]) => void;
}

const SketchEditor = (providers?: Providable): Sketchable => {
  let colorizer: Colorable;
  let isActive: boolean = false;
  let sketch: Sketch | null = null;
  let mapView: MapView | null = null;
  let recordStatus: string | undefined;
  let initialGeometry: GeometricShape | undefined;
  let frozen: boolean = false;
  let hadTypeMismatch: boolean = false;

  const fetchCurrentColor = () => colorizer.getColor(recordStatus);

  const init = (
    newMapView: MapView,
    onGeometryChange: OnGeometryChangeHandler,
    initialValues: InitialValues,
    geometryTypes: GeometricType[],
    status?: string,
  ) => {
    const { form, geometry } = initialValues;
    colorizer = Colorizer(form);
    recordStatus = status;
    frozen = false;

    const color = colorizer.getColor(recordStatus);

    mapView = newMapView;
    initialGeometry = geometry;
    sketch = initialize(mapView, onGeometryChange, fetchCurrentColor, geometryTypes, providers);

    const providable: Providable = providers ?? (Providers(mapView, sketch) as Providable);
    endEditing(providable, sketch, frozen, color, initialGeometry);
  };

  const setNearbyRecords = (nearbyRecords: GeographicRecord[]) => {
    if (!mapView || !sketch || nearbyRecords.length === 0) {
      return;
    }

    const providable: Providable = providers ?? (Providers(mapView, sketch) as Providable);
    drawNearbyRecords(providable, nearbyRecords);
  };

  const compareGeometryToLayer = (geometry, sketchLayer?, geometryMatcher?) => {
    const sketchGeometry = sketchLayer?.graphics?.getItemAt(0)?.geometry;
    const sketchJson = sketchGeometry ? convertToGeoJSON(sketchGeometry) : null;
    return !(geometryMatcher || geometriesMatch)(geometry, sketchJson);
  };

  const evaluateState = (loading, showValidationErrors, editable) => {
    const statesMismatched = isActive !== editable || sketch?.visible !== editable;
    const ableToReceiveDataEvents = !statesMismatched && editable && !mapView?.updating;

    return {
      isRecordBeingSaved: loading,
      didRecordSaveFail: showValidationErrors,
      isInEditingMode: editable,
      statesMismatched,
      ableToReceiveDataEvents,
    };
  };

  const shortCircuitEditRequest = (isRecordBeingSaved, didRecordSaveFail, providable, currentSketch, color) => {
    if (isRecordBeingSaved) {
      frozen = true;
      endEditing(providable, currentSketch, frozen, color);
      return;
    }

    if (didRecordSaveFail) {
      frozen = false;
      isActive = true;
      reenableEditing(providable, currentSketch);
    }
  };

  const handleEditRequest = (
    providable: Providable,
    currentSketch: Sketch,
    color: string,
    types: GeometricType[],
    typeMismatched: boolean,
    editableGeometry: GeometricShape,
    geometry?: GeometricShape | null,
  ) => {
    if (!isActive) {
      endEditing(providable, currentSketch, frozen, color, geometry || initialGeometry);
      return;
    }

    if (!typeMismatched && hadTypeMismatch) {
      hadTypeMismatch = false;
      correctMismatch(providable, currentSketch, types);
      return;
    }

    if (typeMismatched) {
      hadTypeMismatch = true;
      // eslint-disable-next-line no-param-reassign
      currentSketch.visible = false;
      return;
    }

    beginEditing(providable, currentSketch, color, types, editableGeometry);
  };

  const setEditingState = (editorState: EditorState, geometryMatcher?: GeometryMatcher) => {
    if (sketch && mapView) {
      const {
        loading, showValidationErrors, editable, geometry, typeMismatched,
      } = editorState;
      const providable: Providable = providers ?? (Providers(mapView, sketch) as Providable);
      const sketchLayer = providable?.sketchLayer();
      const geometryMismatch = compareGeometryToLayer(geometry, sketchLayer, geometryMatcher);
      const currentEditingState = evaluateState(loading, showValidationErrors, editable);
      const color = colorizer.getColor(recordStatus);
      const {
        isInEditingMode, statesMismatched, isRecordBeingSaved, didRecordSaveFail, ableToReceiveDataEvents,
      } = currentEditingState;

      if (frozen || isRecordBeingSaved || didRecordSaveFail) {
        shortCircuitEditRequest(isRecordBeingSaved, didRecordSaveFail, providable, sketch, color);
        return;
      }

      dumpLayersToConsole(mapView);

      if (ableToReceiveDataEvents && geometryMismatch) {
        replaceShape(providable, geometry, color);
      }

      if (statesMismatched) {
        isActive = isInEditingMode;
        sketch.visible = isActive;

        const types: GeometricType[] = supportedGeometryTypes(mapView);
        const editableGeometry = (geometryMismatch && isInEditingMode) ? geometry : initialGeometry;

        handleEditRequest(
          providable,
          sketch,
          color,
          types,
          !!typeMismatched,
          editableGeometry!,
          geometry,
        );
      }
    }
  };

  const setGeometryColor = (status: string) => {
    if (!sketch || status === recordStatus) {
      return;
    }

    recordStatus = status;

    // @ts-ignore
    const providable: Providable = providers ?? Providers(mapView, sketch);
    const color = colorizer.getColor(recordStatus);

    updateShapeColor(providable, color);
  };

  return {
    init,
    setGeometryColor,
    setEditingState,
    setNearbyRecords,
  };
};

export default SketchEditor;
